/**
 * Persistent in-app order alerts: the dialog + looping sound a merchant, admin or rider gets for a new order,
 * a new ride or a cancellation while the app is open. The backend decides who gets one (fcm.py
 * PERSISTENT_ALERT_TOPICS: staff recipients of tenants with a new-order alert set up) and marks the push with
 * ``data.persistent_alert``; everything here is pure so it can be unit-tested.
 */

const KIND_BY_TOPIC = {
    order_placed: "new_order",
    rider_order_available: "new_ride",
    ride_available: "new_ride",
    order_cancelled: "cancelled",
};

// Formats the WebView can play. .caf/.aiff uploads (iOS notification formats) fall back to the built-in tone.
const PLAYABLE_EXTENSIONS = new Set(["mp3", "wav", "ogg", "m4a", "aac"]);

export const ACK_STORAGE_KEY = "bizil_order_alerts_acked";
export const ACK_TTL_MS = 24 * 60 * 60 * 1000;
const ACK_MAX_ENTRIES = 200;

/**
 * Same opt-in fcm.py uses for the new-order channel: a custom sound or loud mode. This is the LEGACY, whole-tenant
 * switch only — per-event configuration lives in notificationRules.js (``isFullScreenEvent``), which falls back to
 * this when a tenant has saved no rule. Kept for the surfaces that ask "did Super Admin set this tenant up at all".
 */
export function isPersistentAlertEnabled(cfg) {
    return !!(cfg?.sound_key || cfg?.loud_mode_enabled);
}

/** Each staff account can switch persistent alerts off for itself (users.persistent_order_alerts; missing = on). */
export function userWantsPersistentAlerts(user) {
    return user?.persistent_order_alerts !== false;
}

/**
 * Whether a foreground push is a persistent alert. The server marks it only when the recipient's setting allows it
 * at send time, so this trusts the push rather than the account the app loaded earlier (which can be out of date
 * when the setting was changed on another device).
 */
export function isPersistentPush(notif) {
    return notif?.data?.persistent_alert === "1";
}

// Tenants whose own riders are dispatched in-app (backend: tenant.rider_mode, default IN_APP).
const IN_APP_RIDER_MODES = new Set(["IN_APP", "BOTH"]);

/**
 * Whether this rider is one the server sends new-ride pushes to — the same filter as the checkout and merchant
 * status-update broadcasts: the tenant dispatches in-app riders, and the rider is AVAILABLE and approved (pending
 * or rejected riders can't reach the rider API at all). The rider home's own refresh uses it so an offline or
 * on-break rider never gets the loud alert for a ride they weren't sent.
 */
export function riderGetsRideAlerts(me, tenant) {
    const mode = String(tenant?.rider_mode || "IN_APP").toUpperCase();
    const approval = me?.approval_state || "NONE";
    return IN_APP_RIDER_MODES.has(mode) && me?.status === "AVAILABLE" && (approval === "NONE" || approval === "APPROVED");
}

/** A path inside this app to open for "View order", or null. Never follows an off-site URL. */
export function safeInAppPath(url, origin = typeof window !== "undefined" ? window.location.origin : "") {
    if (typeof url !== "string" || !url) return null;
    let path = null;
    if (url.startsWith("/") && !url.startsWith("//") && !url.startsWith("/\\")) {
        path = url;
    } else {
        try {
            const u = new URL(url);
            if (origin && u.origin === origin) path = u.pathname + u.search;
        } catch { /* not a URL */ }
    }
    // Older servers linked rides to /rider/available, which is not a route; rides are listed on the rider home.
    if (path === "/rider/available") return "/rider";
    return path;
}

/** The tenant's uploaded alert sound when the WebView can play it, else null (use the built-in tone). */
export function alertSoundUrl(cfg) {
    const url = cfg?.sound_url;
    if (!cfg?.sound_key || typeof url !== "string" || !/^https?:\/\//i.test(url)) return null;
    const fromUrl = (url.split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i) || [])[1];
    const ext = String(cfg.sound_extension || fromUrl || "").toLowerCase();
    return PLAYABLE_EXTENSIONS.has(ext) ? url : null;
}

/** Build an alert from a foreground push (capacitor.js ``bizil:push`` detail), or null if it isn't one. */
export function alertFromPush(notif, now = Date.now()) {
    const data = notif?.data || {};
    if (data.persistent_alert !== "1") return null;
    const kind = KIND_BY_TOPIC[data.topic];
    const orderId = data.order_id;
    if (!kind || !orderId) return null;
    return {
        key: `${kind}:${orderId}`,
        kind,
        orderId,
        orderNumber: data.order_unique_id || orderId.slice(0, 8),
        title: notif.title || "",
        body: notif.body || "",
        customerName: data.customer_name || "",
        amount: data.grand_total || "",
        itemsCount: data.items_count || "",
        cancelReason: data.cancel_reason || "",
        cancelledBy: data.cancelled_by || "",
        path: safeInAppPath(data.url),
        // How the tenant configured this event (backend notification_policy). Absent on pushes sent before
        // per-event rules existed, which is why both default to on — that was the only behaviour back then.
        repeat: data.alert_repeat !== "0",
        sound: data.alert_sound !== "0",
        receivedAt: now,
    };
}

/** Add an alert unless it was already acknowledged or is already waiting — duplicate pushes never stack. */
export function addAlert(queue, alert, isAcked = () => false) {
    if (!alert?.key || isAcked(alert.key) || queue.some((a) => a.key === alert.key)) return queue;
    return [...queue, alert];
}

export function removeAlert(queue, key) {
    return queue.filter((a) => a.key !== key);
}

function defaultStorage() {
    try { return window.localStorage; } catch { return null; }
}

/**
 * Acknowledged alert keys, kept for a day so a late or repeated push (FCM can redeliver, and the checkout and
 * merchant broadcasts both announce the same ride) never re-opens an alert. Storage failures degrade to memory.
 */
export function createAckStore(storage = defaultStorage(), clock = () => Date.now()) {
    let mem = {};
    const read = () => {
        try {
            const raw = storage?.getItem(ACK_STORAGE_KEY);
            if (raw) mem = JSON.parse(raw) || {};
        } catch { /* keep the in-memory copy */ }
        return mem;
    };
    const write = (entries) => {
        mem = entries;
        try { storage?.setItem(ACK_STORAGE_KEY, JSON.stringify(entries)); } catch { /* memory only */ }
    };
    return {
        has(key) {
            const at = read()[key];
            return typeof at === "number" && clock() - at < ACK_TTL_MS;
        },
        add(key) {
            const now = clock();
            const fresh = Object.entries({ ...read(), [key]: now })
                .filter(([, at]) => typeof at === "number" && now - at < ACK_TTL_MS)
                .sort((a, b) => b[1] - a[1])
                .slice(0, ACK_MAX_ENTRIES);
            write(Object.fromEntries(fresh));
        },
    };
}

export const ORDER_ALERT_EVENT = "bizil:order-alert";
// Fired by capacitor.js when the user taps an OS notification; tapping it acknowledges that order's alert.
export const PUSH_OPENED_EVENT = "bizil:push-opened";

/** Alerts from the web feed (GET /push/my-order-alerts), which returns pushes in the same shape as FCM. */
export function alertsFromFeed(items) {
    return (Array.isArray(items) ? items : []).map((n) => alertFromPush(n)).filter(Boolean);
}

/**
 * Tell the server this account has seen an alert, so it stops re-sending the notification (backend
 * alert_repeats.py). Fire-and-forget: the dialog closes on the user's tap either way, and the repeats are capped
 * anyway, so a failed call costs at most a few more notifications rather than a stuck UI.
 */
export async function ackOrderAlert(key) {
    if (!key) return;
    try {
        const { api } = await import("./api");
        await api.post("/push/alerts/ack", { key });
    } catch { /* offline, signed out, or an older backend without the endpoint */ }
}

/** Raise an alert from in-app polling (same dedupe as pushes). */
export function raiseOrderAlert(alert) {
    try { window.dispatchEvent(new CustomEvent(ORDER_ALERT_EVENT, { detail: alert })); } catch { /* ignore */ }
}
